/**
 * @file basic-details-card.tsx
 * @module app/modules/orders-delivery/components/order-details/basic-details-card
 * @description Displays the basic details of an order, including customer name, order ID, order date, payment method, and order type.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.1
 * @version 2.1.2
 */

import { Card, Col } from "react-bootstrap";
import {
  OrderCancelled,
  OrderSubmitted
} from "../../../../../utils/constants";
import { OrderDetails } from "../../types/order-details";
import OrderInfoRow from "./order-info-row";
import { ORDER_PAYMENT_MODE_LABELS, TRANSACTION_PAYMENT_LABELS } from "src/utils";


interface BasicDetailsCardProps {
  details: OrderDetails;
  getCancelledByName: (order: OrderDetails) => string;
}

/**
 * Returns the payment method label for an order. The latest transaction wins when the order
 * has transactions; otherwise it falls back to the payment mode chosen when the order was placed.
 * Unknown codes give an empty string.
 */
const getPaymentMethodLabel = (details: OrderDetails): string => {
  const { transactions, paymentMode } = details.payment;
  if (transactions.length) {
    const lastTransaction = transactions[transactions.length - 1];
    return TRANSACTION_PAYMENT_LABELS[lastTransaction.paymentMethod] ?? "";
  }
  return ORDER_PAYMENT_MODE_LABELS[paymentMode] ?? "";
};

const BasicDetailsCard = ({ details, getCancelledByName }: BasicDetailsCardProps) => {
  return (
    <Col md={6}>
      <Card className="od-card">
        <Card.Header>
          <Card.Title>Basic details</Card.Title>
          {details.status === OrderSubmitted ? (
            <span className="od-status od-status--warning">Unprocessed</span>
          ) : null}
        </Card.Header>
        <Card.Body>
          <dl className="od-info-list">
            <OrderInfoRow label="Customer name">{details.customer.name}</OrderInfoRow>
            <OrderInfoRow label="Order ID">{details.refKey}</OrderInfoRow>
            <OrderInfoRow label="Order placed on">
              {new Date(details._createdAt).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </OrderInfoRow>
            <OrderInfoRow label="Payment method">
              {getPaymentMethodLabel(details)}
            </OrderInfoRow>
            <OrderInfoRow label="Order type">
              {details?.isPharmacyOrder === true || details?.postData?.isPharmacyOrder === true
                ? "Pharmacy"
                : "Utrade"}
            </OrderInfoRow>

            {details.status === OrderCancelled && (getCancelledByName(details) || details.reason) ? (
              <>
                {getCancelledByName(details) ? (
                  <OrderInfoRow label="Cancelled by">{getCancelledByName(details)}</OrderInfoRow>
                ) : null}
                {details.reason ? (
                  <OrderInfoRow label="Cancellation reason">{details.reason}</OrderInfoRow>
                ) : null}
              </>
            ) : null}

            {details.status !== OrderCancelled && details.modification?.modified && (details.modification?.name || details.reason) ? (
              <>
                {details.modification?.name ? (
                  <OrderInfoRow label="Edited by">{details.modification.name}</OrderInfoRow>
                ) : null}
                {details.reason ? (
                  <OrderInfoRow label="Edit reason">{details.reason}</OrderInfoRow>
                ) : null}
              </>
            ) : null}
          </dl>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default BasicDetailsCard;
